import { useId } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { Icon } from "./icons";

// Input — codified from 08-formlar-girisler.html. Label + text input with the calm
// focus ring (green) and the helper/validation line. Copy (label/hint/error) is the
// caller's — validation logic stays in the surface/server; this only DISPLAYS state.
// The label is associated by id (accessible name), and `error` flips aria-invalid and
// renders the founder's inline alert line.
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** data-testid forwarded to the inline error span (surface tests hook this). */
  errorTestId?: string;
  fieldClassName?: string;
}

export function Input({
  label,
  hint,
  error,
  errorTestId,
  fieldClassName,
  className,
  id,
  ...input
}: InputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = `${inputId}-hint`;
  const errId = `${inputId}-err`;
  const describedBy = error ? errId : hint ? hintId : undefined;

  return (
    <div className={`k-field${fieldClassName ? ` ${fieldClassName}` : ""}`}>
      <label className="k-field__label" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        className={`k-input${error ? " k-input--error" : ""}${className ? ` ${className}` : ""}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...input}
      />
      {hint ? (
        <span id={hintId} className="k-field__hint">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errId} className="k-field__error" data-testid={errorTestId}>
          <Icon name="alert-circle" size={13} strokeWidth={2.2} />
          {error}
        </span>
      ) : null}
    </div>
  );
}
